import { useCallback } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	insertFurnitureIntoCache,
	reorderFurnitureInCache,
	spliceErefIntoCache,
} from "helpers/quotationCache.helper"
import { copyFurniture, readFurnitureClipboard } from "helpers/furnitureClipboard.helper"
import { computeDuplicatePlacement } from "helpers/furniturePlacement.helper"
import AnnotationService from "services/annotations.service"
import type {
	TEref,
	TErefWithFurniture,
	TFurniture,
	TPageElevationData,
	TDuplicateFurnitureRequest,
	TDuplicateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

// REQ-368 furniture copy / paste / duplicate. All state lives in the orchestrator; this hook owns
// only the handlers and receives the stable refs/callbacks it needs. Behavior is unchanged from
// the inline version — the dependency arrays list the passed-in refs/callbacks (all referentially
// stable) explicitly.
export type FurnitureClipboardContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	relatedObjectsRef: MutableRefObject<TPageElevationData[] | undefined>
	duplicateFurnitureAsync: (args: {
		quotationId: number
		furnitureId: number
		body: TDuplicateFurnitureRequest
	}) => Promise<AxiosResponse<TDuplicateFurnitureResponse> | undefined>
	queryClient: QueryClient
	success: (text: string) => void
	formatError: (error: unknown) => void
	handleFurnitureClick: (furniture: TFurniture, options?: { scroll?: boolean }) => void
	findFurnitureById: (furnitureId: number) => TFurniture | undefined
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
}

export const useFurnitureClipboard = (ctx: FurnitureClipboardContext) => {
	const {
		numericQuotationIdRef,
		relatedObjectsRef,
		duplicateFurnitureAsync,
		queryClient,
		success,
		formatError,
		handleFurnitureClick,
		findFurnitureById,
		findErefWithFurnitureById,
	} = ctx

	const copyFurnitureToClipboard = useCallback(
		(furniture: TFurniture) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			copyFurniture(qid, furniture.id)
			success("Object copied")
		},
		[numericQuotationIdRef, success],
	)

	// Shared tail of every duplicate (paste + Duplicate action): call the duplicate endpoint,
	// reconcile the cache, then draw + select the new object. The placement (x0/y0/page) and
	// eref-link decision are made by each caller and handed in via `body`.
	const submitFurnitureDuplicate = useCallback(
		async (qid: number, sourceFurnitureId: number, body: TDuplicateFurnitureRequest) => {
			try {
				const response = await duplicateFurnitureAsync({ quotationId: qid, furnitureId: sourceFurnitureId, body })
				const data = response?.data as TDuplicateFurnitureResponse | undefined
				const created = data?.this_furniture
				if (!created) return

				// Track whether the furniture actually landed in the cache. insertFurnitureIntoCache
				// no-ops (returns the same ref) when no cached page matches created.page_number —
				// drawing the highlight anyway would leave a canvas-only object that vanishes on the
				// next full re-render. In that case fall back to a refetch instead.
				let furnitureInserted = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					// Insert first (the ordering references the new id), then apply the backend-decided
					// group order so the copy lands in the sidebar where the backend put it — not just
					// appended last. Splice the eref last so its bumped version is reflected too.
					let next = insertFurnitureIntoCache(cached, created)
					if (next !== cached) furnitureInserted = true
					if (data?.ordering) next = reorderFurnitureInCache(next, data.ordering)
					return data?.updated_eref ? spliceErefIntoCache(next, data.updated_eref) : next
				})

				if (!furnitureInserted) {
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
					return
				}

				// Draw the new highlight without a full re-render, then select + open its sidebar
				// and center the viewport on the copy so the user sees where it landed.
				AnnotationService.addFurnitureAnnotation(created)
				handleFurnitureClick(created, { scroll: false })
				AnnotationService.selectFurniture(created.id)
				void AnnotationService.scrollToFurniture(created.id)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			}
		},
		[duplicateFurnitureAsync, queryClient, handleFurnitureClick, formatError],
	)

	// Same-page eref rule: keep the eref link (and bump its version) only when the copy lands on
	// the source eref's own page (erefs are page-bound); else leave it unassigned. Mutates `body`.
	const applyErefLink = useCallback(
		(body: TDuplicateFurnitureRequest, source: TFurniture, targetPageNumber: number) => {
			if (source.eref_id == null) return
			const sourceEref: TEref | undefined = findErefWithFurnitureById(source.eref_id)
			if (sourceEref && sourceEref.page_number === targetPageNumber) {
				body.eref_id = source.eref_id
				body.eref_version = sourceEref.version
			}
		},
		[findErefWithFurnitureById],
	)

	const pasteFurnitureAtPoint = useCallback(
		async (point: { pageNumber: number; x: number; y: number }) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const slot = readFurnitureClipboard()
			if (!slot || slot.quotationId !== qid) return
			const source = findFurnitureById(slot.furnitureId)
			if (!source) return

			const body: TDuplicateFurnitureRequest = { x0: point.x, y0: point.y, page_number: point.pageNumber }
			applyErefLink(body, source, point.pageNumber)
			await submitFurnitureDuplicate(qid, slot.furnitureId, body)
		},
		[numericQuotationIdRef, findFurnitureById, applyErefLink, submitFurnitureDuplicate],
	)

	// Duplicate a furniture in place (context-menu Duplicate / ⌘D). Drops a copy on the SAME
	// page, offset to the source's right with collision avoidance (computeDuplicatePlacement);
	// only the new top-left + page is sent, the backend deep-clones the rest as work-in-progress.
	const duplicateFurnitureInPlace = useCallback(
		async (source: TFurniture) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const dims = AnnotationService.getPageDimensions(source.page_number)
			if (!dims) return

			const samePage = relatedObjectsRef.current?.find((p) => p.page_number === source.page_number)
			const occupied = (samePage?.furniture ?? []).filter((f) => f.id !== source.id).map((f) => f.coordinates)
			const { x0, y0 } = computeDuplicatePlacement(source.coordinates, occupied, dims.width, dims.height)

			const body: TDuplicateFurnitureRequest = { x0, y0, page_number: source.page_number }
			applyErefLink(body, source, source.page_number)
			await submitFurnitureDuplicate(qid, source.id, body)
		},
		[numericQuotationIdRef, relatedObjectsRef, applyErefLink, submitFurnitureDuplicate],
	)

	return { copyFurnitureToClipboard, pasteFurnitureAtPoint, duplicateFurnitureInPlace }
}
